import { useContext, useEffect } from 'react';
import useCustomHook from '@/hooks/useCustomHook';
import { GameStageContext } from '../../GameStageContext/GameStageContext';
import { GameAssetsContext } from '../../GameAssetsContext/GameAssetsContext';

// The imageId is necessary to get the originalSize of an image. Use the currentFrame prop instead in case of an animated sprite. Use the pixelSize prop in case of a container game object to set the size of the container in pixels.
export default function useTransform({
  transform,
  imageId,
  currentFrame,
  pixelSize,
  relativeTo,
  spriteScale = 1,
}) {
  const gameStage = useContext(GameStageContext);
  const gameAssets = useContext(GameAssetsContext);
  const { state, actions } = useCustomHook({
    defaultState: {
      responsiveTransform: null,
      originalSize: null,
    },
    actions: {},
  });

  useEffect(() => {
    if (transform && (imageId || pixelSize || currentFrame)) {
      let responsiveLength, scaleAxis, containerWidth, containerHeight;
      if (relativeTo?.current) {
        ({ scaleAxis } = gameStage.state);
        containerWidth = relativeTo.current.offsetWidth;
        containerHeight = relativeTo.current.offsetHeight;
        responsiveLength = scaleAxis == 'x' ? containerWidth : containerHeight;
      } else {
        ({
          responsiveLength,
          scaleAxis,
          width: containerWidth,
          height: containerHeight,
        } = gameStage.state);
      }

      const { width: pixelWidth, height: pixelHeight } =
        pixelSize ?? currentFrame ?? gameAssets.state.images[imageId];

      const scale = Math.abs(
        scaleAxis == 'x'
          ? ((transform.width / 100) * responsiveLength) / pixelWidth
          : ((transform.height / 100) * responsiveLength) / pixelHeight
      );

      // Separate in components to allow flipping in 'x' and/or 'y' directions
      const scaleX = Math.sign(transform.width) * scale * spriteScale;
      const scaleY = Math.sign(transform.height) * scale * spriteScale;

      const x =
        (transform.x / 100) * containerWidth -
        (pixelWidth * scaleX * (transform.pivot?.x ?? 0)) / 100;

      const y =
        (transform.y / 100) * containerHeight -
        (pixelHeight * scaleY * (transform.pivot?.y ?? 0)) / 100;

      const responsiveTransform = {
        x,
        y,
        width: pixelWidth * scale,
        height: pixelHeight * scale,
        scaleX,
        scaleY,
      };

      actions.setState({
        responsiveTransform: {
          ...transform,
          ...responsiveTransform,
        },
        originalSize: {
          width: pixelWidth,
          height: pixelHeight,
        },
      });
    }
  }, [
    relativeTo?.current,
    relativeTo?.current?.offsetWidth,
    relativeTo?.current?.offsetHeight,
    transform.x,
    transform.y,
    transform.width,
    transform.height,
    imageId,
    currentFrame,
    pixelSize?.width,
    pixelSize?.height,
    gameStage.state.responsiveLength,
    gameStage.state.scaleAxis,
    gameStage.state.width,
    gameStage.state.height,
    spriteScale,
  ]);

  return {
    responsiveTransform: state.responsiveTransform,
    originalSize: state.originalSize,
  };
}
